(() => {
  const instances = new WeakMap();

  /**
   * 控制 Cart Footer 的滚动方向显隐：首屏隐藏；用户向上滚动时显示；向下滚动时隐藏；
   * 只要购买区的 Inline Add to Cart 按钮出现在视口内，Cart Footer 就强制保持隐藏。
   * 效果类似移动端浏览器地址栏随滚动方向自动显隐。
   */
  class CollarCartFooterScrollVisibility {
    constructor(root) {
      this.root = root;
      this.footer = root.querySelector('.collar-cart-footer');
      this.inlineButton = root.querySelector('[data-collar-inline-atc-button]');

      // 未启用 Cart Footer block 时（未渲染 .collar-cart-footer）不做任何绑定
      if (!this.footer) return;

      this.lastScrollY = window.scrollY;
      this.scrollWantsVisible = false;
      this.inlineButtonVisible = false;
      this.ticking = false;
      // 滚动阈值：过滤掉惯性/回弹带来的微小滚动抖动
      this.scrollThreshold = 6;

      this.handleScroll = this.handleScroll.bind(this);
      this.handleIntersect = this.handleIntersect.bind(this);

      window.addEventListener('scroll', this.handleScroll, { passive: true });
      this.setupObserver();
      // 初始状态：不加 is-scroll-visible，配合 CSS 默认隐藏，保证首屏无论滚动位置如何都不展示
      this.applyVisibility();
    }

    setupObserver() {
      if (!this.inlineButton || !('IntersectionObserver' in window)) return;
      // 注意：这里不能像一次性 reveal 动画那样 unobserve，
      // 因为按钮进入/离开视口会随着用户滚动反复切换
      this.observer = new IntersectionObserver(this.handleIntersect, { threshold: 0 });
      this.observer.observe(this.inlineButton);
    }

    handleIntersect(entries) {
      entries.forEach((entry) => {
        this.inlineButtonVisible = entry.isIntersecting;
      });
      this.applyVisibility();
    }

    handleScroll() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        const currentY = window.scrollY;
        const delta = currentY - this.lastScrollY;
        if (Math.abs(delta) >= this.scrollThreshold) {
          // 向上滚动时 delta 为负值 -> 希望显示
          this.scrollWantsVisible = delta < 0;
          this.lastScrollY = currentY;
          this.applyVisibility();
        }
        this.ticking = false;
      });
    }

    applyVisibility() {
      const shouldShow = this.scrollWantsVisible && !this.inlineButtonVisible;
      this.footer.classList.toggle('is-scroll-visible', shouldShow);
    }

    destroy() {
      window.removeEventListener('scroll', this.handleScroll);
      this.observer?.disconnect();
      this.footer?.classList.remove('is-scroll-visible');
    }
  }

  /** 初始化指定 DOM 范围内的 Cart Footer 滚动控制，使用 WeakMap 避免重复绑定。 */
  function init(scope = document) {
    const roots = [];
    if (scope.matches?.('[data-collar-cart-footer-scroll]')) roots.push(scope);
    scope.querySelectorAll?.('[data-collar-cart-footer-scroll]').forEach((el) => roots.push(el));
    roots.forEach((root) => {
      if (!instances.has(root)) instances.set(root, new CollarCartFooterScrollVisibility(root));
    });
  }

  document.addEventListener('DOMContentLoaded', () => window.requestAnimationFrame(() => init()));
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const roots = [];
    if (event.target.matches?.('[data-collar-cart-footer-scroll]')) roots.push(event.target);
    event.target.querySelectorAll?.('[data-collar-cart-footer-scroll]').forEach((el) => roots.push(el));
    roots.forEach((root) => {
      instances.get(root)?.destroy();
      instances.delete(root);
    });
  });
})();
